import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';

import { Search, Filter, Download, Eye, Phone, Mail, RotateCcw, Trash2, X, FileText } from 'lucide-react';
import { useSettings } from '../contexts/SettingsContext';

// RESET_TOTAL_COM_SESSAO
axios.defaults.withCredentials = true;

const API_URL = process.env.REACT_APP_API_URL || '/api';

const Leads = () => {
  const { darkMode } = useSettings();
  const [leads, setLeads] = useState([]);
  const [filteredLeads, setFilteredLeads] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('todos');
  const [loading, setLoading] = useState(true);
  const [resettingId, setResettingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  // EXCLUSAO_QUALQUER_LEAD_V23
  // DOCUMENTOS_NO_LEAD_V10
  const [documentLead, setDocumentLead] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [documentsLoading, setDocumentsLoading] = useState(false);
  const [leadStats, setLeadStats] = useState({
    total: 0,
    novos: 0,
    dados_coletados: 0,
    pagos: 0,
    contratos_assinados: 0,
    concluidos: 0
  });

  // CARDS_LEADS_CLICAVEIS_V1
  const selecionarCard = (status) => {
    setSearchTerm('');
    setStatusFilter(status);
  };

  const filterLeads = useCallback(() => {
    let filtered = leads;

    if (statusFilter !== 'todos') {
      // PAGOS_MES_CORRENTE_FINANCEIRO_V1
      filtered = filtered.filter(lead => statusFilter === 'pago'
        ? lead.pago_no_mes === true
        : lead.status === statusFilter);
    }

    if (searchTerm) {
      filtered = filtered.filter(lead =>
        lead.nome.toLowerCase().includes(searchTerm.toLowerCase()) ||
        lead.cpf_cnpj.includes(searchTerm) ||
        lead.telefone.includes(searchTerm)
      );
    }

    setFilteredLeads(filtered);
  }, [leads, searchTerm, statusFilter]);

  useEffect(() => {
    fetchLeads();
  }, []);

  useEffect(() => {
    filterLeads();
  }, [filterLeads]);

  const fetchLeads = async () => {
    try {
        const response = await axios.get(`${API_URL}/lead-overview`);
        setLeads(
          Array.isArray(response.data?.leads)
            ? response.data.leads
            : []
        );

        setLeadStats({
          total:
            Number(response.data?.stats?.total || 0),
          novos:
            Number(response.data?.stats?.novos || 0),
          dados_coletados:
            Number(response.data?.stats?.dados_coletados || 0),
          pagos:
            Number(response.data?.stats?.pagos || 0),
          contratos_assinados:
            Number(response.data?.stats?.contratos_assinados || 0),
          concluidos:
            Number(response.data?.stats?.concluidos || 0)
        });
    } catch (error) {
      console.error('Erro ao buscar leads:', error);
    } finally {
      setLoading(false);
    }
  };

    const resetLeadTeste = async (lead) => {
      const confirmou = window.confirm(
        'Resetar este lead para um novo teste? Os contratos e pagamentos de teste serao encerrados.'
      );

      if (!confirmou) return;

      try {
        setResettingId(lead.id);

        await axios.post(
          `${API_URL}/leads/${lead.id}/reset-test`
        );

        await fetchLeads();

        window.alert(
          'Lead resetado. Envie uma nova mensagem no WhatsApp para recomecar.'
        );
      } catch (error) {
        window.alert(
          error.response?.data?.error ||
          'Nao foi possivel resetar o lead.'
        );
      } finally {
        setResettingId(null);
      }
    };

  const excluirLead = async (lead) => {
    const nome =
      String(lead.nome || 'Não informado')
        .trim();

    const telefone =
      String(lead.telefone || '')
        .trim();

    const identificacao =
      nome +
      (telefone ? ' (' + telefone + ')' : '');

    const confirmou = window.confirm(
      'Excluir definitivamente o lead ' +
      identificacao +
      '?\n\n' +
      'O lead e seus registros vinculados serão removidos do CRM e do fluxo. ' +
      'Esta ação não pode ser desfeita.\n\n' +
      'Contratos ou cobranças já existentes na Assinafy ou no Mercado Pago não serão cancelados.'
    );

    if (!confirmou) return;

    try {
      setDeletingId(lead.id);

      const response =
        await axios.delete(
          `${API_URL}/leads/${lead.id}`
        );

      await fetchLeads();

      window.alert(
        response.data?.message ||
        'Lead excluído com sucesso.'
      );
    } catch (error) {
      window.alert(
        error.response?.data?.error ||
        'Não foi possível excluir o lead.'
      );
    } finally {
      setDeletingId(null);
    }
  };

  const exportToExcel = async () => {
    try {
      const response = await axios.get(`${API_URL}/leads/export/sheet`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `leads_${Date.now()}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Erro ao exportar:', error);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      'novo': 'bg-gray-100 text-gray-800',
      'dados_coletados': 'bg-blue-100 text-blue-800',
      'pago': 'bg-yellow-100 text-yellow-800',
      'contrato_assinado': 'bg-green-100 text-green-800',
      'concluido': 'bg-purple-100 text-purple-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };


  async function abrirDocumentosLead(registro) {
    setDocumentLead(registro);
    setDocuments([]);
    setDocumentsLoading(true);

    try {
      const resposta =
        await axios.get(
          API_URL +
            '/client-documents/lead/' +
            registro.id,
          {
            withCredentials: true
          }
        );

      setDocuments(
        resposta.data?.data || []
      );
    } catch (error) {
      window.alert(
        error.response?.data?.error ||
        'Não foi possível carregar os documentos.'
      );
    } finally {
      setDocumentsLoading(false);
    }
  }

  async function baixarDocumentoLead(documento) {
    try {
      const resposta =
        await axios.get(
          API_URL +
            '/client-documents/' +
            documento.id +
            '/download',
          {
            responseType: 'blob',
            withCredentials: true
          }
        );

      const url =
        window.URL.createObjectURL(
          new Blob([resposta.data])
        );

      const link =
        document.createElement('a');

      link.href = url;
      link.download =
        documento.original_file_name ||
        documento.display_name ||
        'documento';

      document.body.appendChild(link);
      link.click();
      link.remove();

      window.URL.revokeObjectURL(url);
    } catch (error) {
      window.alert(
        'Não foi possível baixar o documento.'
      );
    }
  }


  // VISUALIZAR_DOCUMENTO_LEAD_V11
  async function visualizarDocumentoLead(documento) {
    try {
      const resposta =
        await axios.get(
          API_URL +
            '/client-documents/' +
            documento.id +
            '/download',
          {
            responseType: 'blob',
            withCredentials: true
          }
        );

      const tipo =
        resposta.headers['content-type'] ||
        documento.mime_type ||
        documento.content_type ||
        'application/octet-stream';

      const arquivoBlob =
        new Blob(
          [resposta.data],
          {
            type: tipo
          }
        );

      const url =
        window.URL.createObjectURL(
          arquivoBlob
        );

      const novaAba =
        window.open(
          url,
          '_blank',
          'noopener,noreferrer'
        );

      if (!novaAba) {
        window.URL.revokeObjectURL(url);

        window.alert(
          'O navegador bloqueou a visualização. Permita a abertura de nova aba para este painel.'
        );

        return;
      }

      window.setTimeout(
        () => {
          window.URL.revokeObjectURL(url);
        },
        300000
      );
    } catch (error) {
      window.alert(
        error.response?.data?.error ||
        'Não foi possível visualizar o documento.'
      );
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-gray-500">Carregando...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className={`text-3xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>Leads</h1>
          <p className={darkMode ? 'text-gray-400' : 'text-gray-600'}>Gerencie todos os leads do sistema</p>
        </div>
        <button
          onClick={exportToExcel}
          className="flex items-center gap-2 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors"
        >
          <Download size={20} />
          Exportar Excel
        </button>
      </div>

      {/* Filters */}
      <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-xl shadow-sm p-4 flex flex-col md:flex-row gap-4`}>
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
          <input
            type="text"
            placeholder="Buscar por nome, CPF ou telefone..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className={`w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'border-gray-300'}`}
          />
        </div>
        <div className="flex items-center gap-2">
          <Filter size={20} className="text-gray-400" />
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className={`px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'border-gray-300'}`}
          >
            <option value="todos">Todos os Status</option>
            <option value="novo">Novo</option>
            <option value="dados_coletados">Dados Coletados</option>
            <option value="pago">Pagos no mês</option>
            <option value="contrato_assinado">Contrato Assinado</option>
            <option value="concluido">Concluído</option>
          </select>
        </div>
      </div>

      {/* Stats reais do PostgreSQL */}
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4">
        {[
          { label: 'Total', status: 'todos', value: leadStats.total, color: 'bg-blue-500' },
          { label: 'Novos', status: 'novo', value: leadStats.novos, color: 'bg-gray-500' },
          { label: 'Dados Coletados', status: 'dados_coletados', value: leadStats.dados_coletados, color: 'bg-blue-400' },
          { label: 'Pagos no mês', status: 'pago', value: leadStats.pagos, color: 'bg-yellow-500' },
          { label: 'Contratos Assinados', status: 'contrato_assinado', value: leadStats.contratos_assinados, color: 'bg-green-500' },
          { label: 'Concluídos', status: 'concluido', value: leadStats.concluidos, color: 'bg-purple-500' }
        ].map((stat) => (
          <button
            type="button"
            key={stat.label}
            onClick={() => selecionarCard(stat.status)}
            aria-pressed={statusFilter === stat.status}
            aria-label={stat.status === 'todos' ? 'Mostrar todos os leads' : 'Filtrar leads: ' + stat.label}
            title={stat.status === 'todos' ? 'Mostrar todos os leads' : 'Ver leads: ' + stat.label}
            className={`${stat.color} text-white rounded-xl p-4 text-left cursor-pointer transition-shadow focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-blue-300 ${statusFilter === stat.status ? 'ring-4 ring-blue-200 shadow-lg' : 'hover:shadow-lg'}`}
          >
            <p className="text-sm opacity-90">
              {stat.label}
            </p>
            <p className="text-2xl font-bold">
              {stat.value}
            </p>
          </button>
        ))}
      </div>

      <p className={darkMode ? 'text-gray-400 text-sm' : 'text-gray-600 text-sm'} aria-live="polite">
        {filteredLeads.length} lead(s) exibido(s). Clique em Total para mostrar todos.
      </p>

      {/* Table */}
      <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-xl shadow-sm overflow-hidden`}>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className={darkMode ? 'bg-gray-700' : 'bg-gray-50'}>
              <tr className={`text-left text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                <th className="px-6 py-3">Nome</th>
                <th className="px-6 py-3">CPF/CNPJ</th>
                <th className="px-6 py-3">Contato</th>
                <th className="px-6 py-3">Status</th>
                <th className="px-6 py-3">Valor Total</th>
                <th className="px-6 py-3">Data</th>
                <th className="px-6 py-3">Ações</th>
              </tr>
            </thead>
            <tbody>
              {filteredLeads.length === 0 && (
                <tr><td colSpan={7} className="px-6 py-8 text-center text-gray-500">Nenhum lead encontrado para este filtro.</td></tr>
              )}
              {filteredLeads.map((lead) => (
                <tr key={lead.id} className={`border-t ${darkMode ? 'border-gray-700 hover:bg-gray-700' : 'hover:bg-gray-50'}`}>
                  <td className={`px-6 py-4 font-medium ${darkMode ? 'text-white' : ''}`}>{lead.nome}</td>
                  <td className={`px-6 py-4 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>{lead.cpf_cnpj}</td>
                  <td className="px-6 py-4">
                    <div className="flex flex-col gap-1">
                      <div className="flex items-center gap-1 text-sm">
                        <Phone size={14} className="text-gray-400" />
                        {lead.telefone}
                      </div>
                      <div className="flex items-center gap-1 text-sm">
                        <Mail size={14} className="text-gray-400" />
                        {lead.email}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(lead.status)}`}>
                      {lead.status}
                    </span>
                  </td>
                  <td className={`px-6 py-4 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                    {Number(lead.valor_total || 0) > 0
                      ? Number(lead.valor_total).toLocaleString(
                          'pt-BR',
                          {
                            style: 'currency',
                            currency: 'BRL'
                          }
                        )
                      : '-'}
                  </td>
                  <td className={`px-6 py-4 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                    {new Date(lead.data_criacao).toLocaleDateString('pt-BR')}
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                        <button
                            onClick={() =>
                              abrirDocumentosLead(
                                lead
                              )
                            }
                          className="text-blue-600 hover:text-blue-800"
                          title="Ver documentos"
                        >
                          <Eye size={20} />
                        </button>

                        <button
                          onClick={() => resetLeadTeste(lead)}
                          disabled={resettingId === lead.id || deletingId === lead.id}
                          className="flex items-center gap-1 text-red-600 hover:text-red-800 disabled:opacity-50"
                          title="Resetar lead de teste"
                        >
                          <RotateCcw size={18} />

                          <span className="text-xs">
                            {resettingId === lead.id
                              ? 'Resetando...'
                              : 'Resetar teste'}
                          </span>
                        </button>

                        <button
                          onClick={() => excluirLead(lead)}
                          disabled={deletingId === lead.id || resettingId === lead.id}
                          className="flex items-center gap-1 text-red-600 hover:text-red-800 disabled:opacity-50"
                          title="Excluir lead definitivamente"
                        >
                          <Trash2 size={18} />

                          <span className="text-xs">
                            {deletingId === lead.id
                              ? 'Excluindo...'
                              : 'Excluir'}
                          </span>
                        </button>
                      </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

        {documentLead && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
            <div className={
              'max-h-[90vh] w-full max-w-3xl overflow-y-auto rounded-xl p-6 shadow-xl ' +
              (
                darkMode
                  ? 'bg-gray-800 text-white'
                  : 'bg-white text-gray-900'
              )
            }>
              <div className="flex items-center justify-between gap-3">
                <div>
                  <h2 className="text-xl font-bold">
                    Documentos do lead
                  </h2>
                  <p className={
                    darkMode
                      ? 'text-gray-400'
                      : 'text-gray-600'
                  }>
                    {documentLead.nome}
                  </p>
                </div>

                <button
                  onClick={() => setDocumentLead(null)}
                  className="rounded-lg p-2 hover:bg-gray-500/20"
                  title="Fechar"
                >
                  <X size={22} />
                </button>
              </div>

              {documentsLoading && (
                <p className="mt-5 text-gray-500">
                  Carregando documentos...
                </p>
              )}

              <div className="mt-5 grid gap-3 sm:grid-cols-2">

                {documents.map(documento => (
                  <div
                    key={documento.id}
                    className={
                      'rounded-lg border p-4 ' +
                      (
                        darkMode
                          ? 'border-gray-700'
                          : 'border-gray-200'
                      )
                    }
                  >
                    <div className="flex items-center gap-2">
                      <FileText
                        size={18}
                        className="text-blue-500"
                      />

                      <span className="font-semibold">
                        {documento.display_name ||
                         documento.document_type ||
                         'Documento'}
                      </span>
                    </div>

                    <div className={
                      'mt-2 truncate text-sm ' +
                      (
                        darkMode
                          ? 'text-gray-400'
                          : 'text-gray-600'
                      )
                    }>
                      {documento.original_file_name}
                    </div>

                    <div className="mt-4 flex flex-wrap gap-2">
                      <button
                        onClick={() =>
                          visualizarDocumentoLead(documento)
                        }
                        className="rounded-lg bg-blue-600 px-3 py-2 text-sm font-semibold text-white hover:bg-blue-700"
                      >
                        Visualizar
                      </button>

                      <button
                        onClick={() =>
                          baixarDocumentoLead(
                            documento
                          )
                        }
                        className={
                          'flex items-center gap-2 rounded-lg border px-3 py-2 text-sm font-semibold ' +
                          (
                            darkMode
                              ? 'border-gray-600 text-gray-200 hover:bg-gray-700'
                              : 'border-gray-300 text-gray-700 hover:bg-gray-100'
                          )
                        }
                      >
                        <Download size={16} />
                        Baixar
                      </button>
                    </div>
                  </div>
                ))}

                {!documentsLoading &&
                 documents.length === 0 && (
                  <p className={
                    darkMode
                      ? 'text-gray-400'
                      : 'text-gray-600'
                  }>
                    Nenhum documento recebido.
                  </p>
                )}
              </div>
            </div>
          </div>
        )}

    </div>
  );
};

export default Leads;
